/**
 * Условие доступности режимов, привязанных к чужой раскладке.
 *
 * Пакеты кода и адресов набираются латиницей: ЙЦУКЕН этих символов физически
 * не содержит. Режим переключения раскладок идёт сразу в двух — и незнакомой
 * может оказаться любая из них: русскому новичку чужая латиница, пользователю
 * QWERTY — кириллица. Занятие в незнакомой раскладке не ломается, но состоит
 * из ошибок, а ошибки достаются единицам, в которых пользователь не виноват:
 * клавишам, которых он не знает, и переходам, к которым не готов.
 *
 * Режим предлагается, только когда чужая раскладка освоена (ТЗ FR-EN-36):
 * каждая её буква набирается с оценкой не ниже порога. Раскладки, которых
 * требует режим переключения, берутся из самого режима (ТЗ FR-EN-41) — он и
 * решает, между чем переключается; список в условии разошёлся бы с ним при
 * первой же правке материала.
 *
 * Условие живёт в слое приложения, а не в самих режимах: оно про отношение
 * режима к профилю языка, а функция режима знает только свой материал
 * (docs/ARCHITECTURE.md §5).
 */

import type { LanguageCode, LayoutId, MasteryStat } from '@type2x/shared-contracts';
import { toMasteryUnitId } from '@type2x/shared-contracts';
import { getLayout, supportsCharacter } from '@type2x/keyboard-layouts';
import { isGroupMastered } from '@type2x/learning-engine';
import { keyGroups } from '@type2x/feature-fundamentals';
import { PACK_LAYOUT } from '@type2x/feature-packs';
import { SWITCH_LAYOUTS } from '@type2x/feature-layout-switch';
import type { Workspace } from '@type2x/feature-personal-workspace';
import { activeProfile } from '@type2x/feature-personal-workspace';

/** Состояние условия: выполнено ли, для какой раскладки и сколько осталось. */
export interface ScriptReadiness {
  /** Можно ли предлагать режимы, которым нужна эта раскладка. */
  readonly ready: boolean;
  /** Раскладка, о которой идёт речь. */
  readonly layoutId: LayoutId;
  /** Язык, в профиле которого её тренируют: туда ведёт объяснение. */
  readonly language: LanguageCode;
  /** Сколько её букв набирается уверенно. */
  readonly mastered: number;
  /** Сколько их в раскладке всего. */
  readonly total: number;
  /** Ещё не освоенные буквы в порядке открытия «Основ». */
  readonly missing: readonly string[];
}

/**
 * Буквы раскладки в порядке, в каком их открывают «Основы».
 *
 * Список берётся из самой раскладки тем же кодом, что строит группы клавиш:
 * свой список в коде разошёлся бы с ней при первой же новой раскладке.
 */
export function lettersOf(layoutId: LayoutId): string[] {
  const language = getLayout(layoutId).language;
  return keyGroups({ language, layoutId, units: {}, stats: {} }).flatMap(
    (group) => group.characters,
  );
}

/**
 * Статистика всех профилей пространства, лучшая на единицу.
 *
 * Клавиши раскладки набираются в профиле её языка, а режимы предлагаются в
 * текущем: смотреть только на активный профиль значило бы не заметить уже
 * набранного. Идентификаторы клавиш разных раскладок не совпадают — `key:a` и
 * `key:а` разные единицы, — поэтому объединение профилей ничего не смешивает.
 */
function allStats(workspace: Workspace): Record<string, MasteryStat> {
  const merged: Record<string, MasteryStat> = {};

  for (const profile of Object.values(workspace.profiles)) {
    for (const [unitId, stat] of Object.entries(profile.stats)) {
      const known = merged[unitId];
      if (known === undefined || stat.masteryScore > known.masteryScore) merged[unitId] = stat;
    }
  }

  return merged;
}

/**
 * Набирается ли буква уверенно.
 *
 * Порог тот же, что открывает следующую группу клавиш в «Основах» и режимах
 * символов (ТЗ FR-EN-15): два разных порога означали бы, что «уверенно»
 * значит разное в соседних местах приложения. Проверка идёт тем же кодом —
 * буква здесь и есть группа из одной клавиши.
 */
function isLetterMastered(stats: Readonly<Record<string, MasteryStat>>, letter: string): boolean {
  return isGroupMastered(stats, {
    id: `letter:${letter}`,
    unitIds: [toMasteryUnitId(`key:${letter}`)],
  });
}

/**
 * Тренирует ли пользователь эту раскладку как основную.
 *
 * Для него условие выполнено с самого начала, и это не поблажка: он набирает
 * эти буквы в каждом занятии. Требовать с него отдельного доказательства
 * значило бы закрывать режимы на ровном месте. Сравниваются буквы, а не
 * названия раскладок: две латинские раскладки набирают один и тот же алфавит.
 */
function trainsIn(workspace: Workspace, letters: readonly string[]): boolean {
  const layout = getLayout(activeProfile(workspace).keyboard.layoutId);
  return letters.every((letter) => supportsCharacter(layout, letter));
}

/** Состояние условия для раскладки; без пространства режимов нет вовсе. */
export function scriptReadiness(workspace: Workspace | null, layoutId: LayoutId): ScriptReadiness {
  const letters = lettersOf(layoutId);
  const language = getLayout(layoutId).language;
  const empty = { layoutId, language, total: letters.length };

  if (workspace === null) {
    return { ...empty, ready: false, mastered: 0, missing: letters };
  }

  const stats = allStats(workspace);
  const missing = letters.filter((letter) => !isLetterMastered(stats, letter));

  return {
    ...empty,
    ready: trainsIn(workspace, letters) || missing.length === 0,
    mastered: letters.length - missing.length,
    missing,
  };
}

/** Условие пакетов кода и адресов: их материал латинский целиком. */
export function latinReadiness(workspace: Workspace | null): ScriptReadiness {
  return scriptReadiness(workspace, PACK_LAYOUT);
}

/**
 * Условие режима переключения раскладок: нужны обе раскладки упражнения.
 *
 * Раскладки берутся из самого режима: он и решает, между чем переключается.
 * Возвращается первая неготовая — она и есть та, которой не хватает
 * пользователю; при двух раскладках такая всегда одна, потому что своя
 * раскладка засчитана с самого начала.
 */
export function layoutSwitchReadiness(workspace: Workspace | null): ScriptReadiness {
  const readiness = Object.values(SWITCH_LAYOUTS).map((layoutId) =>
    scriptReadiness(workspace, layoutId),
  );

  return readiness.find((item) => !item.ready) ?? readiness[0]!;
}

/** Короткие проверки для мест, которым важен только ответ «да» или «нет». */
export function latinReady(workspace: Workspace | null): boolean {
  return latinReadiness(workspace).ready;
}

export function layoutSwitchReady(workspace: Workspace | null): boolean {
  return layoutSwitchReadiness(workspace).ready;
}
